import { Bot } from 'lucide-react'
import { useReducedMotion } from 'framer-motion'
import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type Props = {
  stickyTopPx?: number
}

const AutoSetupGeneratingOverlay = ({ stickyTopPx = 0 }: Props) => {
  const reduceMotion = useReducedMotion()
  const overlayRef = useRef<HTMLDivElement>(null)
  const [loaderPos, setLoaderPos] = useState<{ top: number; left: number }>()

  useLayoutEffect(() => {
    const update = () => {
      const el = overlayRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const previewBar = document.querySelector('[data-auto-setup-preview-bar]')
      const previewBarTop =
        previewBar?.getBoundingClientRect().top ?? window.innerHeight
      // The overlay sits above the sticky header (see z-index below), so the loader
      // centers over the whole visible card, clamped to the app chrome (stickyTopPx).
      const visibleTop = Math.max(rect.top, stickyTopPx)
      const visibleBottom = Math.min(rect.bottom, previewBarTop)
      if (visibleBottom - visibleTop < 24) return
      setLoaderPos({
        top: (visibleTop + visibleBottom) / 2,
        left: (rect.left + rect.right) / 2,
      })
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [stickyTopPx])

  const loader = loaderPos ? (
    <div
      className='pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2'
      style={{ top: loaderPos.top, left: loaderPos.left }}
    >
      <div className='flex flex-col items-center gap-5 px-6 text-center'>
        <div className='relative flex h-24 w-24 items-center justify-center'>
          {reduceMotion ? null : (
            <>
              <span className='absolute inset-0 rounded-full border border-info-foreground/30 animate-[ping_1.8s_cubic-bezier(0,0,0.2,1)_infinite]' />
              <span className='absolute inset-3 rounded-full border border-info-foreground/50 animate-[ping_1.8s_cubic-bezier(0,0,0.2,1)_infinite] [animation-delay:400ms]' />
              <span className='absolute inset-0 rounded-full border-2 border-dashed border-info-foreground/80 animate-[spin_8s_linear_infinite]' />
            </>
          )}
          <div className='relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-info text-info-foreground shadow-[0_0_28px] shadow-info-foreground/40'>
            <Bot className='h-7 w-7' />
          </div>
        </div>
        <div className='space-y-1'>
          <p className='text-base font-semibold text-info-foreground'>
            Generating setup
          </p>
          <p className='text-sm text-foreground'>Investigating options...</p>
        </div>
      </div>
    </div>
  ) : null

  return (
    <div
      ref={overlayRef}
      className='absolute inset-0 z-40 rounded-lg bg-background/90 backdrop-blur-sm'
    >
      {loader ? createPortal(loader, document.body) : null}
    </div>
  )
}

export default AutoSetupGeneratingOverlay
